// What a `getSession()` answer means for a route guard (J-210, inbox 06 PR E point 11). Before this,
// `_authenticated.tsx` and `index.tsx` read ANY answer without a session as "signed out" and sent the
// user to the sign-in page: a 429 or a 5xx on `/api/auth/get-session` looked exactly like an ended
// session. Only two answers mean signed out: 200 with no session (Better Auth's answer for no cookie or
// an expired one) and 401. Anything else is "the server could not answer", and the page says so.

export type SessionRead<S> =
  | { kind: "signed-in"; session: S }
  | { kind: "signed-out" }
  | { kind: "unavailable"; status: number };

/** `status` when the server gave no usable answer: none within 15 s, or a body that is not a session. */
export const NO_ANSWER = 0;

/** A session has a user with an id; a malformed body arrives as its text or another shape. */
const isSession = (data: unknown): boolean =>
  typeof data === "object" &&
  data !== null &&
  typeof (data as { user?: { id?: unknown } }).user?.id === "string";

interface SessionAnswer<S> {
  data: S | null | undefined;
  error: { status: number } | null | undefined;
}

export function readSessionAnswer<S>(answer: SessionAnswer<S>): SessionRead<S> {
  // J-215 (inbox 22 item 1): Better Auth's client hands back a body that is not JSON as its text, so a
  // malformed 200 reached the guard as a "session" and the page failed on `session.user`.
  if (answer.data !== null && answer.data !== undefined && answer.data !== "")
    return isSession(answer.data)
      ? { kind: "signed-in", session: answer.data }
      : { kind: "unavailable", status: NO_ANSWER };
  if (answer.error === null || answer.error === undefined) return { kind: "signed-out" };
  if (answer.error.status === 401) return { kind: "signed-out" };
  return { kind: "unavailable", status: answer.error.status };
}

/**
 * J-215 (inbox 22 item 1): the session through `get` (Better Auth's `getSession`). A read that throws
 * (the 15-second read limit, a network failure) is "unavailable", so a hung get-session ends in the
 * busy view with Try again instead of a blank page.
 */
export async function readSession<S>(
  get: () => Promise<SessionAnswer<S>>,
): Promise<SessionRead<S>> {
  const answer = await get().catch(() => undefined);
  return answer === undefined
    ? { kind: "unavailable", status: NO_ANSWER }
    : readSessionAnswer(answer);
}

/** Thrown by a route guard when the session could not be read; the route's error view says "busy". */
export class SessionUnavailableError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(
      status === 429
        ? "The server is busy. Your session is still open; try again in a moment."
        : "The server did not answer. Your session is still open; try again in a moment.",
    );
    this.name = "SessionUnavailableError";
    this.status = status;
  }
}

/**
 * The page to return to after sign-in (J-210, inbox 06 PR E point 4): a path on this site, or "/".
 * `//host/...` and `/\host` are other sites to a browser, so they go to the default page.
 */
/**
 * J-314: a page the server serves itself, outside this app's router (the Learning Academy at `/academy/`; J-362
 * the Quick Reference PDF under `/quick-reference/`). A sign-in that returns to one loads it in full; the router
 * would show its own not-found page instead.
 */
export const isServerPage = (target: string): boolean =>
  target === "/academy" || target.startsWith("/academy/") || target.startsWith("/quick-reference/");

export function safeRedirect(target: string | undefined): string {
  if (target === undefined || !target.startsWith("/") || /^\/[/\\]/.test(target)) return "/";
  // A control character (code below 0x20) can turn a path into another site in some browsers.
  if ([...target].some((c) => c.charCodeAt(0) < 0x20)) return "/";
  const url = new URL(target, "http://same.invalid");
  if (url.origin !== "http://same.invalid") return "/";
  return `${url.pathname}${url.search}${url.hash}`;
}
